import type { ProcessingStatus } from '@clm/shared-types';

/**
 * The pure half of the Admin Pipeline surface — CLM-70. Kept out of
 * `PipelineStatus.tsx` so that file exports only a component and fast refresh
 * stays whole (the `panel-state.ts` split).
 */

/** Used only if the gate ever refuses: every role holds `contract:read`, so this copy is a fallback. */
export const PIPELINE_FORBIDDEN_COPY =
  'The pipeline view reads the contracts in your scope under contract:read, which every CLM role holds. ' +
  'If you see this, your account has no CLM role mapped yet.';

export type StageChip = 'all' | 'Pending' | 'Converted' | 'Pseudonymised' | 'Extracted' | 'Verified' | 'Held';
export const CHIPS: readonly StageChip[] = ['all', 'Pending', 'Converted', 'Pseudonymised', 'Extracted', 'Verified', 'Held'];

/**
 * What each chip's button says — CLM-108 (R-108-14). The chips used to print
 * their key, so the `Verified` chip read "Verified" over every contract at the
 * end of the pipeline, auto-approved ones included. The keys and `chipOf` are
 * unchanged (they group rows; a shared link or a test may name them); only the
 * words move, to the pipeline label `trust-labels.ts` gives `Verified`.
 */
export const CHIP_LABELS: Readonly<Record<StageChip, string>> = {
  all: 'All',
  Pending: 'Pending',
  Converted: 'Converted',
  Pseudonymised: 'Pseudonymised',
  Extracted: 'Extracted',
  Verified: 'Settled',
  Held: 'Held',
};

/**
 * Which chip a row's `processing_status` belongs to — **display grouping
 * only**, over rows the API already returned within scope. Never a scope
 * decision (rule 9).
 */
export function chipOf(status: ProcessingStatus): Exclude<StageChip, 'all'> {
  switch (status) {
    case 'PendingExtraction':
      return 'Pending';
    case 'Converted':
      return 'Converted';
    case 'Pseudonymised':
      return 'Pseudonymised';
    case 'Extracted':
    case 'PendingVerification':
      return 'Extracted';
    case 'Verified':
      return 'Verified';
    case 'ExtractionFailed':
    case 'manual_entry_pending':
      return 'Held';
    default: {
      const exhaustive: never = status;
      return exhaustive;
    }
  }
}
